// src/pages/Campaigns/CampaignDetails.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import {
  FiArrowLeft,
  FiBarChart2,
  FiCheckCircle,
  FiEdit2,
  FiExternalLink,
  FiPauseCircle,
  FiPlayCircle,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";

import { Link, useNavigate, useParams } from "react-router-dom";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";


const PLATFORM_OPTIONS = [
  "Meta",
  "Google",
  "WhatsApp",
  "Website",
  "Other",
];

const OBJECTIVE_OPTIONS = [
  "Lead Generation",
  "Brand Awareness",
  "Traffic",
  "Conversions",
  "Admissions",
  "Other",
];

const PUBLISH_STATUS_OPTIONS = [
  "Not Started",
  "Pending",
  "Partial",
  "Published",
  "Failed",
];


const emptyForm = {
  name: "",
  code: "",
  platform: "Meta",
  objective: "Lead Generation",
  startDate: "",
  endDate: "",
  budget: "",
  spend: "",
  targetAudience: "",
  landingPage: "",
  offer: "",
  metaPublishStatus: "Not Started",
  metaPublishError: "",
  isActive: true,
};


const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};


const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};


const formatCurrency = (value) => {
  const amount = Number(value) || 0;

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};


const formatNumber = (value) => {
  return new Intl.NumberFormat("en-IN").format(
    Number(value) || 0
  );
};


const formatPercentage = (value) => {
  const number = Number(value) || 0;

  return `${number.toFixed(1)}%`;
};


const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};


const getStatusColor = (status) => {
  switch (status) {
    case "Published":
      return "success";

    case "Pending":
      return "warning";

    case "Partial":
      return "info";

    case "Failed":
      return "danger";

    default:
      return "secondary";
  }
};


const getLeadStatusColor = (status) => {
  switch (status) {
    case "New":
      return "primary";

    case "Contacted":
      return "info";

    case "Follow-up":
      return "warning";

    case "Qualified":
      return "success";

    case "Counselling":
      return "info";

    case "Application":
      return "primary";

    case "Admitted":
      return "success";

    case "Lost":
      return "danger";

    default:
      return "secondary";
  }
};


const CampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [breakdown, setBreakdown] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const [publishStatus, setPublishStatus] =
    useState("Not Started");

  const [publishError, setPublishError] =
    useState("");

  const [updatingPublish, setUpdatingPublish] =
    useState(false);

  const [updatingActive, setUpdatingActive] =
    useState(false);


  const fetchCampaign = useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");

        const [
          campaignResponse,
          statsResponse,
          breakdownResponse,
        ] = await Promise.all([
          api.get(`/campaigns/${id}`),
          api.get(`/campaigns/${id}/stats`),
          api.get(`/campaigns/${id}/leads`),
        ]);

        const campaignData =
          campaignResponse?.data?.data;

        const statsData =
          statsResponse?.data?.data;

        const breakdownData =
          breakdownResponse?.data?.data;

        const loadedCampaign =
          campaignData?.campaign ||
          campaignData;

        setCampaign(loadedCampaign || null);

        setStats(
          statsData?.metrics ||
          statsData ||
          null
        );

        setBreakdown(
          breakdownData || null
        );

        if (loadedCampaign) {
          setPublishStatus(
            loadedCampaign.metaPublishStatus ||
            "Not Started"
          );

          setPublishError(
            loadedCampaign.metaPublishError ||
            ""
          );
        }
      } catch (err) {
        console.error(
          "Failed to load campaign details:",
          err
        );

        setError(
          err?.response?.data?.message ||
          "Failed to load campaign details."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id]
  );


  useEffect(() => {
    fetchCampaign(true);
  }, [fetchCampaign]);


  const openEditModal = () => {
    if (!campaign) {
      return;
    }

    setForm({
      name: campaign.name || "",
      code: campaign.code || "",
      platform:
        campaign.platform || "Meta",
      objective:
        campaign.objective ||
        "Lead Generation",
      startDate: toInputDate(
        campaign.startDate
      ),
      endDate: toInputDate(
        campaign.endDate
      ),
      budget:
        campaign.budget ?? "",
      spend:
        campaign.spend ?? "",
      targetAudience:
        campaign.targetAudience || "",
      landingPage:
        campaign.landingPage || "",
      offer:
        campaign.offer || "",
      metaPublishStatus:
        campaign.metaPublishStatus ||
        "Not Started",
      metaPublishError:
        campaign.metaPublishError || "",
      isActive:
        campaign.isActive !== false,
    });

    setFormErrors({});
    setEditing(true);
  };


  const closeEditModal = () => {
    if (saving) {
      return;
    }

    setEditing(false);
    setFormErrors({});
  };


  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));

    if (formErrors[name]) {
      setFormErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }
  };


  const validateForm = () => {
    const errors = {};

    if (!form.name.trim()) {
      errors.name =
        "Campaign name is required.";
    }

    if (!form.code.trim()) {
      errors.code =
        "Campaign code is required.";
    }

    if (
      form.startDate &&
      form.endDate &&
      new Date(form.endDate) <
      new Date(form.startDate)
    ) {
      errors.endDate =
        "End date cannot be before start date.";
    }

    if (
      form.budget !== "" &&
      (Number.isNaN(Number(form.budget)) ||
        Number(form.budget) < 0)
    ) {
      errors.budget =
        "Budget must be a valid non-negative number.";
    }

    if (
      form.spend !== "" &&
      (Number.isNaN(Number(form.spend)) ||
        Number(form.spend) < 0)
    ) {
      errors.spend =
        "Spend must be a valid non-negative number.";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };


  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        platform: form.platform,
        objective: form.objective,
        startDate:
          form.startDate || null,
        endDate:
          form.endDate || null,
        budget:
          form.budget === ""
            ? 0
            : Number(form.budget),
        spend:
          form.spend === ""
            ? 0
            : Number(form.spend),
        targetAudience:
          form.targetAudience.trim(),
        landingPage:
          form.landingPage.trim(),
        offer:
          form.offer.trim(),
        isActive: form.isActive,
      };

      const response =
        await api.put(
          `/campaigns/${id}`,
          payload
        );

      const updated =
        response?.data?.data?.campaign ||
        response?.data?.data;

      if (updated) {
        setCampaign(updated);
      }

      setEditing(false);

      await fetchCampaign(false);
    } catch (err) {
      console.error(
        "Failed to update campaign:",
        err
      );

      const responseErrors =
        err?.response?.data?.errors;

      if (Array.isArray(responseErrors)) {
        const mapped = {};

        responseErrors.forEach(
          (item) => {
            if (item?.field) {
              mapped[item.field] =
                item.message;
            }
          }
        );

        setFormErrors(mapped);
      }

      setError(
        err?.response?.data?.message ||
        "Failed to update campaign."
      );
    } finally {
      setSaving(false);
    }
  };


  const handleToggleActive = async () => {
    if (!campaign) {
      return;
    }

    try {
      setUpdatingActive(true);
      setError("");

      if (campaign.isActive) {
        await api.put(
          `/campaigns/${id}/deactivate`
        );
      } else {
        await api.put(
          `/campaigns/${id}/activate`
        );
      }

      await fetchCampaign(false);
    } catch (err) {
      console.error(
        "Failed to update campaign status:",
        err
      );

      setError(
        err?.response?.data?.message ||
        "Failed to update campaign status."
      );
    } finally {
      setUpdatingActive(false);
    }
  };



  const handlePublishToMeta = async () => {
    console.log("PUBLISH CLICKED", {
    campaignId: id,
    platform: campaign?.platform,
    status: campaign?.metaPublishStatus,
  });

    if (!campaign || campaign.platform !== "Meta") return;

    try {
      setUpdatingPublish(true);
      setPublishError("");
      setError("");
      setSuccess("");

api.post(`/meta/campaigns/${id}/publish`, {
  dailyBudget: Number(campaign.budget),
  privacyPolicyUrl: "https://yatranest.in/privacy-policy.html",
  followUpActionUrl: "https://yatranest.in/",
  targeting: {
    geo_locations: { countries: ["IN"] },
  },
});
      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
          "Meta publishing failed."
        );
      }

      const updated =
        response?.data?.data?.campaign ||
        response?.data?.data;

      if (updated) {
        setCampaign(updated);
        setPublishStatus(
          updated.metaPublishStatus || "Pending"
        );
        setPublishError(updated.metaPublishError || "");
      }

      setSuccess("Meta publishing completed successfully.");
      await fetchCampaign(false);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to publish campaign to Meta.";

      setPublishError(message);
      setError(message);

      await fetchCampaign(false);
    } finally {
      setUpdatingPublish(false);
    }
  };



  const statusMetrics = useMemo(() => {
    return {
      leads:
        Number(stats?.leads) || 0,
      applications:
        Number(stats?.applications) || 0,
      admissions:
        Number(stats?.admissions) || 0,
      spend:
        Number(stats?.spend) || 0,
      budget:
        Number(stats?.budget) || 0,
      costPerLead:
        Number(stats?.costPerLead) || 0,
      costPerApplication:
        Number(
          stats?.costPerApplication
        ) || 0,
      costPerAdmission:
        Number(
          stats?.costPerAdmission
        ) || 0,
      applicationRate:
        Number(stats?.applicationRate) || 0,
      admissionRate:
        Number(stats?.admissionRate) || 0,
    };
  }, [stats]);


  const spendPercentage = useMemo(() => {
    if (!statusMetrics.budget) {
      return 0;
    }

    return Math.min(
      (statusMetrics.spend /
        statusMetrics.budget) *
      100,
      100
    );
  }, [
    statusMetrics.budget,
    statusMetrics.spend,
  ]);


  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "400px",
            }}
          >
            <Spinner
              color="primary"
              className="me-2"
            />
            Loading campaign...
          </div>
        </Container>
      </div>
    );
  }

    document.title =
    "Campaign | eduLeads.Online";

  if (!campaign) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Campaign Details"
            pageTitle="Campaigns"
          />

          <Alert color="danger">
            {error ||
              "Campaign could not be found."}
          </Alert>

          <Button
            color="light"
            onClick={() =>
              navigate("/campaigns")
            }
          >
            <FiArrowLeft
              size={16}
              className="me-1"
            />
            Back to Campaigns
          </Button>
        </Container>
      </div>
    );
  }


  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={campaign.name}
          pageTitle="Campaigns"
        />

        {error && (
          <Alert
            color="danger"
            toggle={() => setError("")}
          >
            {error}
          </Alert>
        )}

        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <Button
                color="light"
                onClick={() =>
                  navigate("/campaigns")
                }
              >
                <FiArrowLeft
                  size={16}
                  className="me-1"
                />
                Back
              </Button>

              <div>
                <h4 className="mb-1">
                  {campaign.name}
                </h4>

                <div className="text-muted">
                  <span className="me-2">
                    {campaign.code}
                  </span>

                  <Badge
                    color={
                      campaign.isActive
                        ? "success"
                        : "secondary"
                    }
                    className="me-2"
                  >
                    {campaign.isActive
                      ? "Active"
                      : "Inactive"}
                  </Badge>

                  <Badge color="light">
                    {campaign.platform}
                  </Badge>
                </div>
              </div>
            </div>
          </Col>

          <Col
            xs="12"
            md="auto"
            className="mt-3 mt-md-0"
          >
            <div className="d-flex gap-2 flex-wrap">
              <Button
                color="light"
                onClick={() =>
                  fetchCampaign(false)
                }
                disabled={refreshing}
              >
                <FiRefreshCw
                  size={15}
                  className="me-1"
                />
                Refresh
              </Button>

              <Button
                color={
                  campaign.isActive
                    ? "warning"
                    : "success"
                }
                onClick={
                  handleToggleActive
                }
                disabled={updatingActive}
              >
                {campaign.isActive ? (
                  <>
                    <FiPauseCircle
                      size={15}
                      className="me-1"
                    />
                    Deactivate
                  </>
                ) : (
                  <>
                    <FiPlayCircle
                      size={15}
                      className="me-1"
                    />
                    Activate
                  </>
                )}
              </Button>

              <Button
                color="primary"
                onClick={openEditModal}
              >
                <FiEdit2
                  size={15}
                  className="me-1"
                />
                Edit Campaign
              </Button>
            </div>
          </Col>
        </Row>


        {/* Metric Cards */}
        <Row>
          <Col
            xl="3"
            md="6"
          >
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-1">
                      Leads
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        statusMetrics.leads
                      )}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary-subtle rounded">
                      <FiUsers
                        size={20}
                        className="text-primary"
                      />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col
            xl="3"
            md="6"
          >
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-1">
                      Applications
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        statusMetrics.applications
                      )}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title bg-info-subtle rounded">
                      <FiTrendingUp
                        size={20}
                        className="text-info"
                      />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col
            xl="3"
            md="6"
          >
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-1">
                      Admissions
                    </p>

                    <h4 className="mb-0">
                      {formatNumber(
                        statusMetrics.admissions
                      )}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title bg-success-subtle rounded">
                      <FiCheckCircle
                        size={20}
                        className="text-success"
                      />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col
            xl="3"
            md="6"
          >
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex justify-content-between">
                  <div>
                    <p className="text-muted mb-1">
                      Spend
                    </p>

                    <h4 className="mb-0">
                      {formatCurrency(
                        statusMetrics.spend
                      )}
                    </h4>
                  </div>

                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning-subtle rounded">
                      <FiBarChart2
                        size={20}
                        className="text-warning"
                      />
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>


        <Row>
          {/* Campaign Information */}
          <Col lg="8">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">
                    Campaign Information
                  </h5>

                  <Badge color="light">
                    {campaign.objective}
                  </Badge>
                </div>
              </CardHeader>

              <CardBody>
                <Row className="gy-3">
                  <Col md="6">
                    <div className="text-muted small">
                      Campaign Name
                    </div>

                    <div className="fw-medium">
                      {campaign.name}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      Campaign Code
                    </div>

                    <div className="fw-medium">
                      {campaign.code}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      Platform
                    </div>

                    <div className="fw-medium">
                      {campaign.platform}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      Objective
                    </div>

                    <div className="fw-medium">
                      {campaign.objective}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      Start Date
                    </div>

                    <div className="fw-medium">
                      {formatDate(
                        campaign.startDate
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      End Date
                    </div>

                    <div className="fw-medium">
                      {formatDate(
                        campaign.endDate
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      Budget
                    </div>

                    <div className="fw-medium">
                      {formatCurrency(
                        campaign.budget
                      )}
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="text-muted small">
                      Spend
                    </div>

                    <div className="fw-medium">
                      {formatCurrency(
                        campaign.spend
                      )}
                    </div>
                  </Col>

                  <Col md="12">
                    <div className="text-muted small mb-1">
                      Spend Utilization
                    </div>

                    <div
                      className="progress"
                      style={{
                        height: "8px",
                      }}
                    >
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${spendPercentage}%`,
                        }}
                      />
                    </div>

                    <div className="text-muted small mt-1">
                      {formatPercentage(
                        spendPercentage
                      )}{" "}
                      of budget used
                    </div>
                  </Col>

                  {campaign.targetAudience && (
                    <Col md="12">
                      <div className="text-muted small">
                        Target Audience
                      </div>

                      <div>
                        {campaign.targetAudience}
                      </div>
                    </Col>
                  )}

                  {campaign.offer && (
                    <Col md="12">
                      <div className="text-muted small">
                        Offer
                      </div>

                      <div>
                        {campaign.offer}
                      </div>
                    </Col>
                  )}

                  {campaign.landingPage && (
                    <Col md="12">
                      <div className="text-muted small">
                        Landing Page
                      </div>

                      <a
                        href={
                          campaign.landingPage
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="d-inline-flex align-items-center"
                      >
                        {campaign.landingPage}

                        <FiExternalLink
                          size={14}
                          className="ms-1"
                        />
                      </a>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          </Col>


          {/* Meta Publishing */}
          <Col lg="4">
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  Meta Publishing
                </h5>
              </CardHeader>

              <CardBody>
                <div className="mb-3">
                  <div className="text-muted small mb-2">
                    Current Status
                  </div>

                  <Badge
                    color={getStatusColor(
                      campaign.metaPublishStatus
                    )}
                    className="px-3 py-2"
                  >
                    {campaign.metaPublishStatus ||
                      "Not Started"}
                  </Badge>
                </div>


                <Button
                  color="primary"
                  className="w-100 mt-3"
                  onClick={handlePublishToMeta}
                  disabled={
                    updatingPublish ||
                    campaign.metaPublishStatus === "Pending" ||
                    campaign.metaPublishStatus === "Published"
                  }
                >
                  {updatingPublish
                    ? "Publishing to Meta..."
                    : campaign.metaPublishStatus === "Published"
                      ? "Published to Meta"
                      : "Publish to Meta"}
                </Button>


                {campaign.metaPublishError && (
                  <Alert
                    color="danger"
                    className="mt-3 mb-0"
                  >
                    <div className="fw-medium mb-1">
                      Publishing Error
                    </div>

                    <div className="small">
                      {
                        campaign.metaPublishError
                      }
                    </div>
                  </Alert>
                )}

                <hr />

                <div className="small text-muted">
                  Meta Campaign ID
                </div>

                <div className="small text-break mb-2">
                  {campaign.externalCampaignId ||
                    "Not published"}
                </div>

                <div className="small text-muted">
                  Meta Ad Set ID
                </div>

                <div className="small text-break mb-2">
                  {campaign.externalAdSetId ||
                    "Not published"}
                </div>

                <div className="small text-muted">
                  Meta Lead Form ID
                </div>

                <div className="small text-break">
                  {campaign.externalFormId ||
                    "Not published"}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>


        {/* Performance */}
        <Row>
          <Col lg="12">
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  Performance
                </h5>
              </CardHeader>

              <CardBody>
                <Row className="gy-4">
                  <Col
                    md="3"
                    sm="6"
                  >
                    <div className="text-muted small">
                      Cost per Lead
                    </div>

                    <h5 className="mt-1 mb-0">
                      {formatCurrency(
                        statusMetrics.costPerLead
                      )}
                    </h5>
                  </Col>

                  <Col
                    md="3"
                    sm="6"
                  >
                    <div className="text-muted small">
                      Cost per Application
                    </div>

                    <h5 className="mt-1 mb-0">
                      {formatCurrency(
                        statusMetrics.costPerApplication
                      )}
                    </h5>
                  </Col>

                  <Col
                    md="3"
                    sm="6"
                  >
                    <div className="text-muted small">
                      Cost per Admission
                    </div>

                    <h5 className="mt-1 mb-0">
                      {formatCurrency(
                        statusMetrics.costPerAdmission
                      )}
                    </h5>
                  </Col>

                  <Col
                    md="3"
                    sm="6"
                  >
                    <div className="text-muted small">
                      Application Rate
                    </div>

                    <h5 className="mt-1 mb-0">
                      {formatPercentage(
                        statusMetrics.applicationRate
                      )}
                    </h5>
                  </Col>

                  <Col
                    md="3"
                    sm="6"
                  >
                    <div className="text-muted small">
                      Admission Rate
                    </div>

                    <h5 className="mt-1 mb-0">
                      {formatPercentage(
                        statusMetrics.admissionRate
                      )}
                    </h5>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>


        {/* Lead Breakdown */}
        <Row>
          <Col lg="4">
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  Leads by Status
                </h5>
              </CardHeader>

              <CardBody className="p-0">
                {breakdown?.byStatus?.length ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0"
                    >
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th className="text-end">
                            Leads
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {breakdown.byStatus.map(
                          (item, index) => (
                            <tr
                              key={`${item._id}-${index}`}
                            >
                              <td>
                                <Badge
                                  color={getLeadStatusColor(
                                    item._id
                                  )}
                                >
                                  {item._id ||
                                    "Unknown"}
                                </Badge>
                              </td>

                              <td className="text-end fw-medium">
                                {formatNumber(
                                  item.count
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    No lead status data available.
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>


          <Col lg="4">
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  Leads by Source
                </h5>
              </CardHeader>

              <CardBody className="p-0">
                {breakdown?.bySource?.length ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0"
                    >
                      <thead>
                        <tr>
                          <th>Source</th>
                          <th>Platform</th>
                          <th className="text-end">
                            Leads
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {breakdown.bySource.map(
                          (item, index) => (
                            <tr
                              key={`${index}`}
                            >
                              <td>
                                {item?._id
                                  ?.source ||
                                  "Unknown"}
                              </td>

                              <td>
                                {item?._id
                                  ?.platform ||
                                  "—"}
                              </td>

                              <td className="text-end fw-medium">
                                {formatNumber(
                                  item.count
                                )}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    No source data available.
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>


          <Col lg="4">
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  Leads by Month
                </h5>
              </CardHeader>

              <CardBody className="p-0">
                {breakdown?.byMonth?.length ? (
                  <div className="table-responsive">
                    <Table
                      hover
                      className="mb-0"
                    >
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th className="text-end">
                            Leads
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {breakdown.byMonth.map(
                          (item, index) => {
                            const year =
                              item?._id?.year;

                            const month =
                              item?._id?.month;

                            const monthName =
                              year &&
                                month
                                ? new Date(
                                  year,
                                  month - 1,
                                  1
                                ).toLocaleString(
                                  "en-IN",
                                  {
                                    month:
                                      "short",
                                  }
                                )
                                : "Unknown";

                            return (
                              <tr
                                key={`${year}-${month}-${index}`}
                              >
                                <td>
                                  {monthName}{" "}
                                  {year || ""}
                                </td>

                                <td className="text-end fw-medium">
                                  {formatNumber(
                                    item.count
                                  )}
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    No monthly data available.
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>


        {/* Campaign Meta IDs */}
        <Row>
          <Col lg="12">
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  External Platform IDs
                </h5>
              </CardHeader>

              <CardBody>
                <Row className="gy-3">
                  <Col md="4">
                    <div className="text-muted small">
                      Campaign ID
                    </div>

                    <div className="text-break">
                      {campaign.externalCampaignId ||
                        "—"}
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="text-muted small">
                      Ad Set ID
                    </div>

                    <div className="text-break">
                      {campaign.externalAdSetId ||
                        "—"}
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="text-muted small">
                      Creative ID
                    </div>

                    <div className="text-break">
                      {campaign.externalCreativeId ||
                        "—"}
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="text-muted small">
                      Ad ID
                    </div>

                    <div className="text-break">
                      {campaign.externalAdId ||
                        "—"}
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="text-muted small">
                      Lead Form ID
                    </div>

                    <div className="text-break">
                      {campaign.externalFormId ||
                        "—"}
                    </div>
                  </Col>

                  <Col md="4">
                    <div className="text-muted small">
                      Created
                    </div>

                    <div>
                      {formatDateTime(
                        campaign.createdAt
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>


        {/* Edit Modal */}
        <Modal
          isOpen={editing}
          toggle={closeEditModal}
          size="lg"
          centered
        >
          <ModalHeader toggle={closeEditModal}>
            Edit Campaign
          </ModalHeader>

          <ModalBody>
            <Row className="g-3">
              <Col md="8">
                <Label>
                  Campaign Name{" "}
                  <span className="text-danger">
                    *
                  </span>
                </Label>

                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  invalid={!!formErrors.name}
                  placeholder="Campaign name"
                />

                <FormFeedback>
                  {formErrors.name}
                </FormFeedback>
              </Col>

              <Col md="4">
                <Label>
                  Campaign Code{" "}
                  <span className="text-danger">
                    *
                  </span>
                </Label>

                <Input
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  invalid={!!formErrors.code}
                  placeholder="CAMPAIGN_2026"
                />

                <FormFeedback>
                  {formErrors.code}
                </FormFeedback>
              </Col>

              <Col md="6">
                <Label>
                  Platform
                </Label>

                <Input
                  type="select"
                  name="platform"
                  value={form.platform}
                  onChange={handleChange}
                >
                  {PLATFORM_OPTIONS.map(
                    (platform) => (
                      <option
                        value={platform}
                        key={platform}
                      >
                        {platform}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col md="6">
                <Label>
                  Objective
                </Label>

                <Input
                  type="select"
                  name="objective"
                  value={form.objective}
                  onChange={handleChange}
                >
                  {OBJECTIVE_OPTIONS.map(
                    (objective) => (
                      <option
                        value={objective}
                        key={objective}
                      >
                        {objective}
                      </option>
                    )
                  )}
                </Input>
              </Col>

              <Col md="6">
                <Label>
                  Start Date
                </Label>

                <Input
                  type="date"
                  name="startDate"
                  value={form.startDate}
                  onChange={handleChange}
                />
              </Col>

              <Col md="6">
                <Label>
                  End Date
                </Label>

                <Input
                  type="date"
                  name="endDate"
                  value={form.endDate}
                  onChange={handleChange}
                  invalid={
                    !!formErrors.endDate
                  }
                />

                <FormFeedback>
                  {formErrors.endDate}
                </FormFeedback>
              </Col>

              <Col md="6">
                <Label>
                  Budget
                </Label>

                <Input
                  type="number"
                  min="0"
                  name="budget"
                  value={form.budget}
                  onChange={handleChange}
                  invalid={
                    !!formErrors.budget
                  }
                  placeholder="0"
                />

                <FormFeedback>
                  {formErrors.budget}
                </FormFeedback>
              </Col>

              <Col md="6">
                <Label>
                  Spend
                </Label>

                <Input
                  type="number"
                  min="0"
                  name="spend"
                  value={form.spend}
                  onChange={handleChange}
                  invalid={
                    !!formErrors.spend
                  }
                  placeholder="0"
                />

                <FormFeedback>
                  {formErrors.spend}
                </FormFeedback>
              </Col>

              <Col md="12">
                <Label>
                  Target Audience
                </Label>

                <Input
                  type="textarea"
                  rows="2"
                  name="targetAudience"
                  value={
                    form.targetAudience
                  }
                  onChange={handleChange}
                  placeholder="Describe the target audience"
                />
              </Col>

              <Col md="12">
                <Label>
                  Landing Page
                </Label>

                <Input
                  type="url"
                  name="landingPage"
                  value={
                    form.landingPage
                  }
                  onChange={handleChange}
                  placeholder="https://example.com/admissions"
                />
              </Col>

              <Col md="12">
                <Label>
                  Offer
                </Label>

                <Input
                  type="textarea"
                  rows="2"
                  name="offer"
                  value={form.offer}
                  onChange={handleChange}
                  placeholder="Campaign offer or message"
                />
              </Col>

              <Col md="12">
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="campaign-active"
                    name="isActive"
                    checked={
                      form.isActive
                    }
                    onChange={handleChange}
                  />

                  <Label
                    for="campaign-active"
                    className="form-check-label"
                  >
                    Campaign is active
                  </Label>
                </div>
              </Col>
            </Row>
          </ModalBody>

          <ModalFooter>
            <Button
              color="light"
              onClick={closeEditModal}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-1"
                  />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};


export default CampaignDetails;